/**
 * Assembly marks for poster sheets that get taped together.
 *
 * When a poster spans several sheets, neighbouring sheets share a strip of the
 * image — the overlap. That strip is what the glue goes on, so it must survive
 * onto the finished poster; cutting it away would defeat the point of printing
 * it twice.
 *
 * Each seam therefore has two halves, and every sheet carries both roles:
 *
 *   • **Glue area** on the right and bottom edges — the neighbour gets laid on
 *     top here, so the mark ends up hidden underneath it.
 *   • **Cut line** on the left and top edges — this sheet is trimmed before it
 *     goes on top of its neighbour, which also removes the white margin most
 *     printers leave. The mark leaves with the offcut.
 *
 * The printer's unprintable margin drives the numbers. A sheet is rendered
 * edge to edge, but a normal printer leaves ~5 mm blank all round, so the
 * outermost millimetres of the image never reach the paper. That margin is
 * eaten twice at every seam — once on each sheet — which is why the overlap
 * has to cover it twice over:
 *
 *     overlap >= trim + printerMargin + glueArea
 *
 * With a 20 mm overlap and an 8 mm trim, a printer losing d mm still leaves
 * 12 − d mm of *printed* overlap to glue and align on. Trimming 8 mm also
 * removes the upper sheet's own white margin, which would otherwise sit as a
 * white stripe across the finished poster.
 *
 *     sheet 1 (below)                sheet 2 (on top)
 *     ┌──────────────┬──────┐        ┌──────┬──────────────┐
 *     │              │ glue │        │ cut  │              │
 *     └──────────────┴──────┘        └──────┴──────────────┘
 *                     12mm             8mm
 *                     └───── 20mm overlap ─────┘
 *
 * Everything is drawn straight onto the target 2D context in paper space (mm),
 * so it is unaffected by however the surface scaled its contents.
 */

import glueStickUrl from "./assets/glue-stick.png";
import {
  GLUE_GLYPH_SIZE_MM,
  GLYPH_SIZE_MM,
  MARK_STYLE,
  type PageSheetMarks,
  SCISSORS_VIEW_H,
  SCISSORS_VIEW_W,
  SCISSORS_W_MM,
  sheetMarkGeometry,
} from "./sheet-mark-geometry.js";

/** Scissors glyph, traced from `docs/assets/schere.svg` (potrace output). */
const SCISSORS_PATH =
  "M1805 7520 c-566 -63 -1064 -432 -1228 -910 -55 -160 -72 -278 -72 -490 1 -177 3 -205 27 -300 106 -420 352 -758 712 -975 182 -109 349 -161 999 -310 612 -141 920 -238 1258 -397 321 -152 648 -369 902 -599 42 -39 77 -73 77 -77 0 -4 -24 -18 -53 -31 -634 -287 -1208 -386 -2043 -352 -515 21 -982 26 -1087 11 -627 -85 -1162 -594 -1273 -1210 -25 -136 -25 -397 -1 -510 91 -420 386 -782 806 -991 340 -168 776 -218 1132 -129 278 69 558 231 738 426 227 247 325 509 338 904 7 201 -6 278 -73 437 -87 209 -72 257 96 307 104 31 281 53 690 86 617 50 1021 100 1457 180 l162 30 443 -229 c2754 -1420 4198 -2161 4268 -2192 142 -61 338 -122 495 -154 161 -32 238 -39 425 -39 213 0 319 14 490 64 229 67 385 150 619 329 89 69 120 98 110 104 -8 4 -828 446 -1824 980 -995 535 -2073 1114 -2395 1287 -555 299 -583 315 -550 321 64 14 5315 1210 5333 1215 13 4 17 17 17 58 0 48 -5 59 -55 126 -295 394 -692 622 -1215 696 -139 19 -460 22 -605 4 -55 -6 -1088 -218 -2295 -471 -1207 -253 -2368 -496 -2580 -540 -212 -44 -400 -88 -419 -98 l-33 -18 -252 122 c-399 195 -733 336 -1231 520 -679 251 -796 302 -878 382 -61 59 -49 124 47 243 133 164 185 285 232 540 24 133 30 376 10 486 -90 517 -545 979 -1101 1118 -187 47 -440 65 -620 46z m300 -620 c211 -30 353 -95 492 -224 217 -202 322 -492 280 -776 -30 -205 -110 -365 -245 -492 -181 -170 -478 -237 -748 -168 -345 88 -644 377 -730 705 -13 50 -18 105 -18 190 0 108 3 129 32 212 84 246 182 363 387 463 177 87 361 117 550 90z m-317 -4416 c158 -32 311 -113 423 -225 61 -61 86 -95 123 -170 68 -138 81 -198 81 -364 -1 -153 -20 -247 -75 -371 -107 -240 -327 -426 -585 -495 -121 -33 -325 -31 -457 4 -180 48 -333 134 -451 254 -131 133 -189 273 -203 486 -27 424 321 803 816 890 63 11 256 6 328 -9z";

interface DrawArgs {
  ctx: CanvasRenderingContext2D;
  /** Sheet size in millimetres, already resolved for its orientation. */
  wMm: number;
  hMm: number;
  /** Canvas size in device pixels. */
  pixelW: number;
  pixelH: number;
  marks: PageSheetMarks;
}

function rgba(c: readonly [number, number, number, number]): string {
  return `rgba(${c[0]},${c[1]},${c[2]},${c[3]})`;
}

let glueImage: HTMLImageElement | null = null;
let glueImagePromise: Promise<HTMLImageElement | null> | null = null;

/**
 * Load the glue-stick bitmap once and reuse it. Resolves to null if it fails
 * so a broken asset costs the marks their icon, not the whole export.
 */
function loadGlueImage(): Promise<HTMLImageElement | null> {
  if (glueImage) return Promise.resolve(glueImage);
  if (!glueImagePromise) {
    glueImagePromise = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        glueImage = img;
        resolve(img);
      };
      img.onerror = () => resolve(null);
      img.src = glueStickUrl;
    });
  }
  return glueImagePromise;
}

export async function drawSheetMarks({
  ctx,
  wMm,
  hMm,
  pixelW,
  pixelH,
  marks,
}: DrawArgs): Promise<void> {
  const geometry = sheetMarkGeometry(marks, wMm, hMm);
  const { glueTint, glueLines, glueGlyphs, cutLines, scissors } = geometry;
  if (
    glueTint.length + glueLines.length + glueGlyphs.length + cutLines.length + scissors.length ===
    0
  ) {
    return;
  }

  const pxPerMmX = pixelW / wMm;
  const pxPerMmY = pixelH / hMm;
  const pxPerMm = Math.min(pxPerMmX, pxPerMmY);

  const glue = glueGlyphs.length > 0 ? await loadGlueImage() : null;

  ctx.save();

  if (glueTint.length > 0) {
    // All strips go into ONE path so the fill is a single operation.
    ctx.beginPath();
    for (const r of glueTint) {
      ctx.rect(r.xMm * pxPerMmX, r.yMm * pxPerMmY, r.wMm * pxPerMmX, r.hMm * pxPerMmY);
    }
    ctx.fillStyle = rgba(MARK_STYLE.glueTint);
    ctx.fill();

    ctx.strokeStyle = rgba(MARK_STYLE.glueInk);
    ctx.lineWidth = Math.max(1, MARK_STYLE.glueLineWidthMm * pxPerMm);
    ctx.setLineDash(MARK_STYLE.glueDashMm.map((d) => d * pxPerMm));
    ctx.beginPath();
    for (const l of glueLines) {
      ctx.moveTo(l.x1Mm * pxPerMmX, l.y1Mm * pxPerMmY);
      ctx.lineTo(l.x2Mm * pxPerMmX, l.y2Mm * pxPerMmY);
    }
    ctx.stroke();
    ctx.setLineDash([]);

    for (const g of glueGlyphs) {
      drawGlue(ctx, glue, g.cxMm * pxPerMmX, g.cyMm * pxPerMmY, pxPerMm, g.vertical);
    }
  }

  if (cutLines.length > 0) {
    ctx.strokeStyle = rgba(MARK_STYLE.cutInk);
    ctx.fillStyle = rgba(MARK_STYLE.cutInk);
    ctx.lineWidth = Math.max(1, MARK_STYLE.cutLineWidthMm * pxPerMm);
    ctx.setLineDash(MARK_STYLE.cutDashMm.map((d) => d * pxPerMm));
    for (const l of cutLines) {
      ctx.beginPath();
      ctx.moveTo(l.x1Mm * pxPerMmX, l.y1Mm * pxPerMmY);
      ctx.lineTo(l.x2Mm * pxPerMmX, l.y2Mm * pxPerMmY);
      ctx.stroke();
      // Scissors belong to the line running in their direction.
      const lineVertical = l.x1Mm === l.x2Mm;
      for (const s of scissors) {
        if (s.vertical === lineVertical) {
          paintScissors(ctx, s.cxMm * pxPerMmX, s.cyMm * pxPerMmY, pxPerMm, s.vertical);
        }
      }
    }
  }

  ctx.restore();
}

function drawGlue(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement | null,
  cx: number,
  cy: number,
  pxPerMm: number,
  vertical: boolean,
): void {
  if (!img) return;
  const size = GLUE_GLYPH_SIZE_MM * pxPerMm;
  ctx.save();
  ctx.setLineDash([]);
  ctx.globalAlpha = MARK_STYLE.glueGlyphOpacity;
  ctx.translate(cx, cy);
  if (vertical) ctx.rotate(Math.PI / 2);
  ctx.drawImage(img, -size / 2, -size / 2, size, size);
  ctx.restore();
}

/**
 * Paint one scissors glyph centred on (cx, cy). `vertical` turns it a quarter
 * turn so it runs along a vertical cut instead of a horizontal one.
 */
export function paintScissors(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  pxPerMm: number,
  vertical: boolean,
): void {
  const height = GLYPH_SIZE_MM * pxPerMm;
  const scale = height / SCISSORS_VIEW_H;
  ctx.save();
  ctx.setLineDash([]);
  ctx.translate(cx, cy);
  if (vertical) ctx.rotate(Math.PI / 2);
  ctx.scale(scale, scale);
  ctx.translate(-SCISSORS_VIEW_W / 2, -SCISSORS_VIEW_H / 2);
  // Undo the potrace export transform baked into the source SVG's <g>.
  ctx.translate(0, SCISSORS_VIEW_H);
  ctx.scale(0.1, -0.1);
  ctx.fill(new Path2D(SCISSORS_PATH));
  ctx.restore();
}

/**
 * Scissors glyph as PNG bytes for pdf-lib. The bitmap has exactly the glyph
 * box's aspect (`SCISSORS_W_MM × GLYPH_SIZE_MM`), so it never distorts.
 */
export async function renderScissorsPng(pxPerMm = 24): Promise<Uint8Array> {
  const wMm = SCISSORS_W_MM;
  const w = Math.ceil(wMm * pxPerMm);
  const h = Math.ceil(GLYPH_SIZE_MM * pxPerMm);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  try {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Scissors canvas has no 2D context.");
    ctx.fillStyle = rgba(MARK_STYLE.cutInk);
    // Rounding the pixel size up must not change the aspect: stretch the
    // glyph by the (tiny) rounding factor so it fills the bitmap exactly.
    ctx.scale(w / (wMm * pxPerMm), h / (GLYPH_SIZE_MM * pxPerMm));
    paintScissors(ctx, (wMm * pxPerMm) / 2, (GLYPH_SIZE_MM * pxPerMm) / 2, pxPerMm, false);
    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
        "image/png",
      );
    });
    return new Uint8Array(await blob.arrayBuffer());
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}

/** Glue-stick bitmap as PNG bytes, or null if it cannot be loaded. */
export async function loadGlueStickPng(): Promise<Uint8Array | null> {
  try {
    const res = await fetch(glueStickUrl);
    if (!res.ok) return null;
    return new Uint8Array(await res.arrayBuffer());
  } catch {
    return null;
  }
}
